import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { orderAPI, authAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { Package, User, MapPin, ChevronDown, ChevronUp } from 'lucide-react';

const STATUS_COLORS = {
  pending: '#f59e0b', processing: '#3b82f6', shipped: '#8b5cf6',
  delivered: '#10b981', cancelled: '#ef4444',
};

const inputStyle = { width: '100%', padding: '10px 14px', background: 'var(--dark)', border: '1px solid var(--border)', color: 'var(--white)', fontSize: '13px', marginBottom: '12px' };

const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [tab, setTab] = useState('orders');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [profileData, setProfileData] = useState({ name: user?.name || '', phone: user?.phone || '', address: user?.address || {} });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    orderAPI.getMy().then(({ data }) => setOrders(data)).catch(() => {}).finally(() => setLoadingOrders(false));
  }, []);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await authAPI.updateProfile(profileData);
      updateUser(data);
      toast.success('Profile updated!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update profile');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ paddingTop: '72px', minHeight: '100vh' }}>
      <div style={{ background: 'var(--dark)', padding: '40px 0', borderBottom: '1px solid var(--border)' }}>
        <div className="container">
          <p style={{ fontSize: '10px', letterSpacing: '0.3em', color: 'var(--gold)', marginBottom: '8px' }}>ACCOUNT</p>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '40px', fontWeight: 400 }}>My Profile</h1>
          <p style={{ color: 'var(--white-dim)', fontSize: '13px', marginTop: '6px' }}>{user?.name} · {user?.email}</p>
        </div>
      </div>

      <div className="container" style={{ paddingTop: '40px', paddingBottom: '80px' }}>
        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid var(--border)', marginBottom: '40px' }}>
          {[['orders', Package, 'My Orders'], ['profile', User, 'Profile'], ['address', MapPin, 'Address']].map(([id, Icon, label]) => (
            <button key={id} onClick={() => setTab(id)}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '14px 24px', background: 'none', color: tab === id ? 'var(--gold)' : 'var(--white-dim)', fontSize: '12px', letterSpacing: '0.15em', fontWeight: 500, borderBottom: `2px solid ${tab === id ? 'var(--gold)' : 'transparent'}`, cursor: 'pointer', transition: 'all 0.3s' }}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {/* Orders Tab */}
        {tab === 'orders' && (
          <div>
            {loadingOrders ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[...Array(3)].map((_, i) => <div key={i} className="skeleton" style={{ height: '80px' }} />)}
              </div>
            ) : orders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px', color: 'var(--white-dim)' }}>
                <Package size={48} color="var(--border)" style={{ marginBottom: '16px' }} />
                <p style={{ fontFamily: 'var(--font-display)', fontSize: '24px', marginBottom: '8px' }}>No orders yet</p>
                <p style={{ fontSize: '13px' }}>Your order history will appear here</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {orders.map(order => (
                  <div key={order._id} style={{ background: 'var(--dark-2)', border: '1px solid var(--border)' }}>
                    <button onClick={() => setExpandedOrder(expandedOrder === order._id ? null : order._id)}
                      style={{ width: '100%', display: 'grid', gridTemplateColumns: '1fr auto auto auto auto', gap: '16px', alignItems: 'center', padding: '20px 24px', background: 'none', color: 'var(--white)', cursor: 'pointer', textAlign: 'left' }}>
                      <div>
                        <p style={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>{order.orderNumber}</p>
                        <p style={{ fontSize: '11px', color: 'var(--white-dim)' }}>{new Date(order.createdAt).toLocaleDateString()}</p>
                      </div>
                      <span style={{ fontSize: '11px', padding: '4px 10px', border: `1px solid ${STATUS_COLORS[order.orderStatus] || 'var(--border)'}`, color: STATUS_COLORS[order.orderStatus], letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                        {order.orderStatus}
                      </span>
                      <span style={{ fontSize: '11px', padding: '4px 10px', border: '1px solid var(--border)', color: order.paymentStatus === 'paid' ? '#10b981' : 'var(--white-dim)', letterSpacing: '0.1em' }}>
                        {order.paymentStatus}
                      </span>
                      <span style={{ fontFamily: 'var(--font-display)', fontSize: '20px', color: 'var(--gold)' }}>${order.totalPrice}</span>
                      {expandedOrder === order._id ? <ChevronUp size={16} color="var(--white-dim)" /> : <ChevronDown size={16} color="var(--white-dim)" />}
                    </button>

                    {expandedOrder === order._id && (
                      <div style={{ borderTop: '1px solid var(--border)', padding: '20px 24px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
                          {order.items.map((item, i) => (
                            <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                              <div style={{ width: '48px', height: '48px', background: 'var(--dark-3)', flexShrink: 0, overflow: 'hidden' }}>
                                {item.image && <img src={item.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                              </div>
                              <div style={{ flex: 1 }}>
                                <p style={{ fontSize: '13px' }}>{item.name}</p>
                                <p style={{ fontSize: '11px', color: 'var(--white-dim)' }}>Qty: {item.quantity} · ${item.price}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '12px', color: 'var(--white-dim)' }}>
                          <div>
                            <p style={{ fontWeight: 600, color: 'var(--white)', marginBottom: '6px' }}>Shipping To</p>
                            <p>{order.shippingAddress?.fullName}</p>
                            <p>{order.shippingAddress?.street}</p>
                            <p>{order.shippingAddress?.city}, {order.shippingAddress?.state}</p>
                            <p>{order.shippingAddress?.country} {order.shippingAddress?.zipCode}</p>
                          </div>
                          {order.trackingNumber && (
                            <div>
                              <p style={{ fontWeight: 600, color: 'var(--white)', marginBottom: '6px' }}>Tracking</p>
                              <p style={{ color: 'var(--gold)' }}>{order.trackingNumber}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Profile Tab */}
        {tab === 'profile' && (
          <form onSubmit={handleSaveProfile} style={{ maxWidth: '500px' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '24px', fontWeight: 400, marginBottom: '24px' }}>Personal Details</h3>
            <label style={{ fontSize: '11px', letterSpacing: '0.15em', color: 'var(--white-dim)', display: 'block', marginBottom: '6px' }}>FULL NAME</label>
            <input value={profileData.name} onChange={e => setProfileData(p => ({ ...p, name: e.target.value }))} style={inputStyle} />
            <label style={{ fontSize: '11px', letterSpacing: '0.15em', color: 'var(--white-dim)', display: 'block', marginBottom: '6px' }}>EMAIL</label>
            <input value={user?.email} disabled style={{ ...inputStyle, opacity: 0.5, cursor: 'not-allowed' }} />
            <label style={{ fontSize: '11px', letterSpacing: '0.15em', color: 'var(--white-dim)', display: 'block', marginBottom: '6px' }}>PHONE</label>
            <input value={profileData.phone} onChange={e => setProfileData(p => ({ ...p, phone: e.target.value }))} style={inputStyle} placeholder="+1 234 567 890" />
            <button type="submit" disabled={saving} style={{ background: 'var(--gold)', color: 'var(--black)', padding: '12px 32px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.2em', cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'SAVING...' : 'SAVE CHANGES'}
            </button>
          </form>
        )}

        {/* Address Tab */}
        {tab === 'address' && (
          <form onSubmit={handleSaveProfile} style={{ maxWidth: '500px' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '24px', fontWeight: 400, marginBottom: '24px' }}>Delivery Address</h3>
            {[
              { key: 'street', label: 'STREET ADDRESS', placeholder: '123 Main Street' },
              { key: 'city', label: 'CITY', placeholder: 'New York' },
              { key: 'state', label: 'STATE', placeholder: 'NY' },
              { key: 'zipCode', label: 'ZIP CODE', placeholder: '10001' },
              { key: 'country', label: 'COUNTRY', placeholder: 'United States' },
            ].map(({ key, label, placeholder }) => (
              <div key={key}>
                <label style={{ fontSize: '11px', letterSpacing: '0.15em', color: 'var(--white-dim)', display: 'block', marginBottom: '6px' }}>{label}</label>
                <input value={profileData.address?.[key] || ''} onChange={e => setProfileData(p => ({ ...p, address: { ...p.address, [key]: e.target.value } }))} style={inputStyle} placeholder={placeholder} />
              </div>
            ))}
            <button type="submit" disabled={saving} style={{ background: 'var(--gold)', color: 'var(--black)', padding: '12px 32px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.2em', cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'SAVING...' : 'SAVE ADDRESS'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
